import { ArrowRight, CheckCircle2, Clock3, Mail, MessageSquareText, PencilLine, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import {
  getConfig,
  getJourneyAgreement,
  getJourneyPayment,
  getJourneyProposal,
  submitAgreementAction,
  submitProposalAction,
} from "../../lib/api";
import type { JourneyAgreement, JourneyPayment, JourneyProposal, JourneyProposalItem, VaultConfig } from "../../lib/types";

export function ProposalAcceptancePage() {
  const { token = "" } = useParams();
  const [proposal, setProposal] = useState<JourneyProposal | null>(null);
  const [lineItems, setLineItems] = useState<JourneyProposalItem[]>([]);
  const [config, setConfig] = useState<VaultConfig | null>(null);
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Proposal Review | Kreatethedate";
    void Promise.all([getJourneyProposal(token), getConfig()])
      .then(([data, cfg]) => {
        setProposal(data.proposal);
        setLineItems(data.lineItems || []);
        setConfig(cfg);
      })
      .catch((loadError) => setError(loadError instanceof Error ? loadError.message : "Unable to load proposal."))
      .finally(() => setLoading(false));
  }, [token]);

  const services = useMemo(() => splitCsv(proposal?.selectedServices), [proposal?.selectedServices]);
  const addons = useMemo(() => splitCsv(proposal?.recommendedAddons), [proposal?.recommendedAddons]);

  async function handleAction(action: "accept" | "request_changes" | "decline") {
    if (!proposal) return;
    setSaving(true);
    setError("");
    try {
      const result = await submitProposalAction(token, { action, comments });
      if (action === "accept" && result.agreementUrl) {
        window.location.href = result.agreementUrl;
        return;
      }
      if (action === "decline") {
        setComments("");
      }
      setError(`Status updated to ${result.status}.`);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Unable to update proposal.");
    } finally {
      setSaving(false);
    }
  }

  const bookingUrl = config?.discoveryCallBookingUrl || "";

  return (
    <JourneyPageShell
      eyebrow="Proposal Acceptance"
      title="Review Your Kreatethedate Proposal"
      loading={loading}
      error={error}
      action={
        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => void handleAction("accept")}
            disabled={saving}
            className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a] disabled:opacity-60"
          >
            <CheckCircle2 className="size-4" />
            Accept Proposal
          </button>
          <button
            type="button"
            onClick={() => void handleAction("request_changes")}
            disabled={saving}
            className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-[#D94A16]/24 px-5 text-sm font-extrabold text-[#332a1f] transition hover:border-[#FF6A2B] hover:bg-[#fff9ef] disabled:opacity-60"
          >
            <PencilLine className="size-4" />
            Request Changes
          </button>
          <a
            href={bookingUrl || "#"}
            target={bookingUrl ? "_blank" : undefined}
            rel={bookingUrl ? "noreferrer" : undefined}
            className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-[#D94A16]/24 px-5 text-sm font-extrabold text-[#332a1f] transition hover:border-[#FF6A2B] hover:bg-[#fff9ef]"
          >
            <Clock3 className="size-4" />
            Schedule Follow-Up Call
          </a>
        </div>
      }
    >
      {proposal ? (
        <div className="grid gap-6">
          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <JourneyRoadmap
              currentStep="Proposal Review"
              completed={["Discovery Call Complete"]}
              upcoming={["Agreement", "Payment", "Welcome", "Project Planning", "Delivery"]}
            />
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <InfoCard label="Proposal Number" value={proposal.proposalNumber} />
              <InfoCard label="Package" value={proposal.packageName} />
              <InfoCard label="Subtotal" value={money(proposal.subtotalCents)} />
              <InfoCard label="Total Project Investment" value={money(proposal.totalCents)} />
              <InfoCard label="Deposit" value={money(proposal.depositCents)} />
              <InfoCard label="Balance Due" value={money(proposal.balanceDueCents)} />
            </div>
          </section>

          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <SectionHeading title="Proposal Summary" eyebrow="Your Investment" />
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              <InfoCard label="Recommended services" value={services.join(", ") || proposal.recommendedServices || "Not provided"} />
              <InfoCard label="Recommended add-ons" value={addons.join(", ") || proposal.recommendedAddons || "Not provided"} />
            </div>
            <div className="mt-5 grid gap-3">
              {lineItems.map((item) => (
                <div key={item.id} className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-2xl font-bold text-[#050505]">{item.name}</p>
                      <p className="mt-1 text-sm font-semibold text-[#5c5246]">{item.description || item.lineType}</p>
                    </div>
                    <p className="text-sm font-black text-[#050505]">{money(item.amountCents)}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <SectionHeading title="Change Requests" eyebrow="Need Adjustments?" />
            <p className="mt-4 text-sm font-semibold leading-7 text-[#5c5246]">
              If you would like to adjust the proposal, add a short note and submit Request Changes. We will review your
              request and follow up with the right next step.
            </p>
            <textarea
              value={comments}
              onChange={(event) => setComments(event.target.value)}
              className="form-field focus-ring mt-4 min-h-32 w-full rounded-lg px-4 py-3 text-sm font-semibold"
              placeholder="Tell us what you would like to adjust"
            />
          </section>
        </div>
      ) : null}
    </JourneyPageShell>
  );
}

export function AgreementSignaturePage() {
  const { token = "" } = useParams();
  const [agreement, setAgreement] = useState<JourneyAgreement | null>(null);
  const [proposal, setProposal] = useState<JourneyProposal | null>(null);
  const [lineItems, setLineItems] = useState<JourneyProposalItem[]>([]);
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Agreement | Kreatethedate";
    void getJourneyAgreement(token)
      .then((data) => {
        setAgreement(data.agreement);
        setProposal(data.proposal);
        setLineItems(data.lineItems || []);
        setSignerName(data.agreement.signerName || "");
        setSignerEmail(data.agreement.signerEmail || "");
      })
      .catch((loadError) => setError(loadError instanceof Error ? loadError.message : "Unable to load agreement."))
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSign() {
    setSaving(true);
    setError("");
    try {
      const result = await submitAgreementAction(token, {
        action: "sign",
        signerName,
        signerEmail,
      });
      if (result.paymentUrl) {
        window.location.href = result.paymentUrl;
        return;
      }
      setError(`Agreement status updated to ${result.status}.`);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Unable to update agreement.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <JourneyPageShell
      eyebrow="Agreement Signature"
      title="Review & Sign Your Service Agreement"
      loading={loading}
      error={error}
      action={
        <button
          type="button"
          onClick={() => void handleSign()}
          disabled={saving}
          className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a] disabled:opacity-60"
        >
          <Sparkles className="size-4" />
          Review & Sign Agreement
        </button>
      }
    >
      {agreement && proposal ? (
        <div className="grid gap-6">
          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <JourneyRoadmap currentStep="Agreement Signature" completed={["Discovery Call", "Proposal Accepted"]} upcoming={["Payment", "Welcome", "Project Planning", "Delivery"]} />
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <InfoCard label="Agreement status" value={agreement.status} />
              <InfoCard label="Proposal number" value={proposal.proposalNumber} />
              <InfoCard label="Package" value={proposal.packageName} />
              <InfoCard label="Total investment" value={money(proposal.totalCents)} />
            </div>
          </section>

          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <SectionHeading title="Signature Details" eyebrow="Internal agreement workflow" />
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <label className="grid gap-2 text-sm font-bold text-[#332a1f]">
                Full Name
                <input
                  value={signerName}
                  onChange={(event) => setSignerName(event.target.value)}
                  className="form-field focus-ring min-h-12 rounded-lg px-4 text-sm font-semibold"
                  placeholder="Your full name"
                />
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#332a1f]">
                Email
                <input
                  value={signerEmail}
                  onChange={(event) => setSignerEmail(event.target.value)}
                  className="form-field focus-ring min-h-12 rounded-lg px-4 text-sm font-semibold"
                  placeholder="you@example.com"
                />
              </label>
            </div>
            <div className="mt-5 grid gap-3">
              {lineItems.map((item) => (
                <div key={item.id} className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-2xl font-bold text-[#050505]">{item.name}</p>
                      <p className="mt-1 text-sm font-semibold text-[#5c5246]">{item.description || item.lineType}</p>
                    </div>
                    <p className="text-sm font-black text-[#050505]">{money(item.amountCents)}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      ) : null}
    </JourneyPageShell>
  );
}

export function PaymentPage() {
  const { token = "" } = useParams();
  const [payment, setPayment] = useState<JourneyPayment | null>(null);
  const [proposal, setProposal] = useState<JourneyProposal | null>(null);
  const [lineItems, setLineItems] = useState<JourneyProposalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Payment | Kreatethedate";
    void getJourneyPayment(token)
      .then((data) => {
        setPayment(data.payment);
        setProposal(data.proposal);
        setLineItems(data.lineItems || []);
      })
      .catch((loadError) => setError(loadError instanceof Error ? loadError.message : "Unable to load payment."))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <JourneyPageShell
      eyebrow="Booking Payment"
      title="Complete Your Kreatethedate Booking"
      loading={loading}
      error={error}
      action={
        payment?.stripeCheckoutUrl ? (
          <a
            href={payment.stripeCheckoutUrl}
            className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a]"
          >
            <ArrowRight className="size-4" />
            Complete Your Booking Payment
          </a>
        ) : null
      }
    >
      {payment && proposal ? (
        <div className="grid gap-6">
          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <JourneyRoadmap currentStep="Booking Payment" completed={["Discovery Call", "Proposal", "Agreement"]} upcoming={["Welcome", "Planning", "Project Creation", "Delivery"]} />
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <InfoCard
                label="Amount due now"
                value={money(payment.amountPaidCents > 0 ? payment.amountRemainingCents : payment.depositCents || payment.totalCents)}
              />
              <InfoCard label="Deposit amount" value={money(payment.depositCents)} />
              <InfoCard label="Remaining balance" value={money(payment.amountRemainingCents)} />
              <InfoCard label="Invoice status" value={payment.invoiceStatus || payment.status} />
              <InfoCard label="Tax" value={payment.taxLabel ? `${payment.taxLabel} (${payment.taxPercentage || 0}%)` : "Not applied"} />
              <InfoCard label="Tax amount" value={money(payment.taxCents || 0)} />
              <InfoCard label="Payment status" value={payment.status} />
            </div>
          </section>

          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <SectionHeading title="Booking Summary" eyebrow="Approved proposal totals" />
            <div className="mt-5 grid gap-3">
              {lineItems.map((item) => (
                <div key={item.id} className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-2xl font-bold text-[#050505]">{item.name}</p>
                    <p className="mt-1 text-sm font-semibold text-[#5c5246]">{item.description || item.lineType}</p>
                  </div>
                  <p className="text-sm font-black text-[#050505]">{money(item.amountCents)}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      ) : null}
    </JourneyPageShell>
  );
}

export function OnboardingCenterPage() {
  const { token = "" } = useParams();
  const [proposal, setProposal] = useState<JourneyProposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Onboarding Center | Kreatethedate";
    void getJourneyProposal(token)
      .then((data) => setProposal(data.proposal))
      .catch((loadError) => setError(loadError instanceof Error ? loadError.message : "Unable to load onboarding center."))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <JourneyPageShell
      eyebrow="Onboarding Center"
      title="Let's Gather Everything We Need"
      loading={loading}
      error={error}
      action={
        <a
          href="/client/dashboard"
          className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a]"
        >
          <Mail className="size-4" />
          Open Client Portal
        </a>
      }
    >
      {proposal ? (
        <div className="grid gap-6">
          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <JourneyRoadmap currentStep="Planning & Onboarding" completed={["Booking Complete"]} upcoming={["Build", "Review", "Approval", "Delivery"]} />
          </section>

          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <SectionHeading title="What We Need From You" eyebrow="Onboarding checklist" />
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {[
                "Client details",
                "Project details",
                "Theme",
                "Color preferences",
                "Important dates",
                "Guest count",
                "Media assets",
                "Music preferences",
                "Special instructions",
                "Uploads",
                "Additional requests",
              ].map((item) => (
                <div key={item} className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 text-sm font-bold text-[#332a1f]">
                  {item}
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a
                href="/client/instructions"
                className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a]"
              >
                <Sparkles className="size-4" />
                View Project Instructions
              </a>
              <a
                href="/client/upload-link"
                className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-[#D94A16]/24 px-5 text-sm font-extrabold text-[#332a1f] transition hover:border-[#FF6A2B] hover:bg-[#fff9ef]"
              >
                <MessageSquareText className="size-4" />
                Upload Materials
              </a>
            </div>
          </section>
        </div>
      ) : null}
    </JourneyPageShell>
  );
}

function JourneyPageShell({
  action,
  children,
  error,
  eyebrow,
  loading,
  title,
}: {
  action?: ReactNode;
  children: ReactNode;
  error: string;
  eyebrow: string;
  loading: boolean;
  title: string;
}) {
  return (
    <main className="min-h-screen bg-[#fff9ef] text-[#16120a]">
      <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,106,43,.16),transparent_36%),linear-gradient(120deg,rgba(5,5,5,.96),rgba(22,18,10,.9))]" />
        <div className="relative mx-auto grid max-w-6xl gap-5">
          <p className="inline-flex w-fit items-center gap-2 rounded-full border border-[#FF6A2B]/35 px-3 py-2 text-xs font-black uppercase tracking-[0.2em] text-[#FF6A2B]">
            <Sparkles className="size-4" />
            {eyebrow}
          </p>
          <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">{title}</h1>
          <p className="max-w-3xl text-base font-semibold leading-8 text-[#fff9ef]/76">
            A guided, premium client journey that keeps every next step clear and personal.
          </p>
          {action ? <div>{action}</div> : null}
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-6">
          {error ? <Alert message={error} /> : null}
          {loading ? <LoadingState /> : null}
          {!loading ? children : null}
        </div>
      </section>
    </main>
  );
}

function JourneyRoadmap({
  currentStep,
  completed,
  upcoming,
}: {
  currentStep: string;
  completed: string[];
  upcoming: string[];
}) {
  return (
    <div className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Journey Roadmap</p>
      <p className="mt-2 text-sm font-bold text-[#332a1f]">Current Step: {currentStep}</p>
      <p className="mt-1 text-sm font-semibold text-[#5c5246]">Completed: {completed.join(" • ") || "Inquiry Submitted"}</p>
      <p className="mt-1 text-sm font-semibold text-[#5c5246]">Upcoming: {upcoming.join(" • ")}</p>
    </div>
  );
}

function SectionHeading({ title, eyebrow }: { title: string; eyebrow: string }) {
  return (
    <div className="border-b border-[#D94A16]/16 pb-4">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{eyebrow}</p>
      <h2 className="mt-1 font-display text-3xl font-bold text-[#050505]">{title}</h2>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{label}</p>
      <p className="mt-2 text-sm font-bold leading-6 text-[#332a1f]">{value || "Not provided"}</p>
    </div>
  );
}

function Alert({ message }: { message: string }) {
  return <p className="rounded-lg border border-red-300 bg-red-50 px-5 py-4 text-sm font-bold text-red-800">{message}</p>;
}

function LoadingState() {
  return (
    <div className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-6 text-center">
      <p className="font-display text-2xl font-bold text-[#050505]">Loading journey details</p>
      <p className="mt-2 text-sm font-semibold text-[#5c5246]">Preparing your guided client experience.</p>
    </div>
  );
}

function splitCsv(value?: string) {
  return value
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];
}

function money(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((Number(cents) || 0) / 100);
}
